/**
 * Copyright (c) 2025 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState, useRef, useEffect } from 'react';
import type { WrappedInput } from 'id-components/WrappedInput';
import { MaterialIcon, InputContainer } from '@blend/enterprise-components';
import { css, StyleSheet } from 'aphrodite';

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  toggleButton: {
    position: 'absolute',
    right: '8px',
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    pointerEvents: 'auto',
  },
});

export const PasswordField: React.FC<WrappedInput.Props> = props => {
  const [showPassword, setShowPassword] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [buttonStyle, setButtonStyle] = useState({});

  useEffect(() => {
    const positionButton = () => {
      if (!containerRef.current) return;

      const inputElement = containerRef.current.querySelector('input');
      if (!inputElement) return;

      // Position button relative to input
      const inputRect = inputElement.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const top = inputRect.top - containerRect.top + (inputRect.height - 28) / 2;

      setButtonStyle({
        top: `${top}px`,
      });
    };

    // Position button initially and on window resize
    positionButton();
    window.addEventListener('resize', positionButton);

    // Cleanup
    return () => {
      window.removeEventListener('resize', positionButton);
    };
  }, []);

  const enhancedInputProps = {
    ...props,
    customComponentProps: {
      ...props.customComponentProps,
      type: showPassword ? 'text' : 'password',
      style: {
        ...props.customComponentProps?.style,
        paddingRight: '40px',
      },
    },
  };

  const rightAddon = (
    <button
      id={`it-PasswordVisibilityToggle-${props.input.name}`}
      type="button"
      onClick={() => setShowPassword(!showPassword)}
      className={css(styles.toggleButton)}
      style={buttonStyle}
      aria-label={showPassword ? 'Hide password' : 'Show password'}
    >
      <MaterialIcon type={showPassword ? 'visibility_off' : 'visibility'} size={20} color="#6B7280" />
    </button>
  );

  return (
    <div className={css(styles.container)} ref={containerRef}>
      <InputContainer {...enhancedInputProps} />
      {rightAddon}
    </div>
  );
};
